/** Parst ARKO-Antworten (Portal/n8n) auf LEX-Stempel, Normverweise und MEM-Zitate. */

const LEX_STAMP_RE = /\[LEX:[^\]]+\]/g;
const MEM_STAMP_RE = /\[MEM:([^\]]+)\]/g;
const NORM_CITE_RE = /\[([A-Za-z][A-Za-z-]{1,11}-\d+[a-z]?(?:-\d+)?)\]/g;
const RIS_LINE_RE = /\(RIS:\s*([^)·]+)(?:\s*·\s*gueltig ab\s*([^)]+))?\)/gi;

export interface ArkoLexStamp {
  kind: 'lex_stamp';
  raw: string;
}

export interface ArkoNormRef {
  kind: 'norm';
  normId: string;
}

export interface ArkoRisRef {
  kind: 'ris';
  url: string;
  validFrom?: string;
}

export interface ArkoMemRef {
  kind: 'mem';
  /** Roher Stempel-Inhalt, z. B. „MEM-2026-000001 · 2026-06-01“. */
  raw: string;
  vermerkId?: string;
  date?: string;
}

export interface ArkoQuoteRef {
  kind: 'quote';
  normId: string;
  quote: string;
}

export type ArkoSourceItem = ArkoLexStamp | ArkoNormRef | ArkoRisRef | ArkoMemRef | ArkoQuoteRef;

const QUOTE_NORM_RE = /"([^"]+)"\s*\[([A-Za-z][A-Za-z-]{1,11}-\d+[a-z]?(?:-\d+)?)\]/g;

export function extractArkoSources(text: string): ArkoSourceItem[] {
  const items: ArkoSourceItem[] = [];
  const seen = new Set<string>();

  const push = (key: string, item: ArkoSourceItem) => {
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  for (const m of text.matchAll(LEX_STAMP_RE)) {
    push(`lex:${m[0]}`, { kind: 'lex_stamp', raw: m[0] });
  }

  for (const m of text.matchAll(MEM_STAMP_RE)) {
    const inner = m[1].trim();
    const parts = inner.split('·').map((s) => s.trim());
    push(`mem:${inner}`, {
      kind: 'mem',
      raw: inner,
      vermerkId: parts[0] || undefined,
      date: parts[1] || undefined,
    });
  }

  for (const m of text.matchAll(NORM_CITE_RE)) {
    const normId = m[1].toUpperCase();
    if (normId.startsWith('LEX')) continue;
    push(`norm:${normId}`, { kind: 'norm', normId });
  }

  for (const m of text.matchAll(QUOTE_NORM_RE)) {
    const quote = m[1].trim();
    const normId = m[2].toUpperCase();
    if (quote.length > 0) {
      push(`quote:${normId}:${quote.slice(0, 40)}`, { kind: 'quote', normId, quote });
    }
  }

  let risMatch: RegExpExecArray | null;
  RIS_LINE_RE.lastIndex = 0;
  while ((risMatch = RIS_LINE_RE.exec(text)) !== null) {
    const url = risMatch[1].trim();
    const validFrom = risMatch[2]?.trim();
    push(`ris:${url}`, { kind: 'ris', url, validFrom: validFrom || undefined });
  }

  return items;
}

export function hasArkoSources(text: string): boolean {
  return extractArkoSources(text).length > 0;
}

export function hasArkoLexSources(text: string): boolean {
  return extractArkoSources(text).some(
    (item) =>
      item.kind === 'lex_stamp' ||
      item.kind === 'norm' ||
      item.kind === 'ris' ||
      item.kind === 'quote',
  );
}

/** Wortlaut-Zitate aus Hard-Quote-Antworten (Fallback ohne n8n-lexProof). */
export function extractLexQuotesFromText(text: string): Array<{ norm_id: string; fragment_text: string }> {
  return extractArkoSources(text)
    .filter((item): item is ArkoQuoteRef => item.kind === 'quote')
    .map((item) => ({ norm_id: item.normId, fragment_text: item.quote }));
}

export function collectLexNormIds(text: string, lexProof?: { sources?: Array<{ norm_id?: string }> } | null): string[] {
  const fromProof = (lexProof?.sources ?? [])
    .map((s) => String(s.norm_id || '').trim())
    .filter(Boolean);
  if (fromProof.length > 0) return fromProof;
  return extractArkoSources(text)
    .filter((item): item is ArkoNormRef | ArkoQuoteRef => item.kind === 'norm' || item.kind === 'quote')
    .map((item) => item.normId);
}

export function hasArkoMemSources(text: string): boolean {
  return extractArkoSources(text).some((item) => item.kind === 'mem');
}
